/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("TableSortUIBehavior", ["UIBehaviorBase"], function (context, cls) {
  /**
   * @class TableSortUIBehavior
   * @memberOf classes
   * @extends classes.UIBehaviorBase
   */
  cls.TableSortUIBehavior = context.oo.Singleton(cls.UIBehaviorBase, function ($super) {
    return /** @lends classes.TableSortUIBehavior.prototype */ {
      /** @type {string} */
      __name: "TableSortUIBehavior",
      /**
       * @inheritDoc
       */
      _attachWidget: function (controller, data) {
        let widget = controller.getWidget();
        if (widget) {
          data.clickHandle = widget.when(gbc.constants.widgetEvents.tableHeaderSort, this._sort.bind(this, controller, data));
          data.setSortSpecHandle = widget.when(gbc.constants.widgetEvents.tableSetSortSpec, this._applySortRules.bind(this, controller));
        }
      },
      /**
       * @inheritDoc
       */
      _detachWidget: function (controller, data) {
        if (data.clickHandle) {
          data.clickHandle();
          data.clickHandle = null;
        }
        if (data.setSortSpecHandle) {
          data.setSortSpecHandle();
          data.setSortSpecHandle = null;
        }
      },

      /**
       * Sort table
       * @param {classes.ControllerBase} controller
       * @param {Object} data
       * @param {Object} opt
       * @private
       */
      _sort: function (controller, data, opt) {
        const tableWidget = /** @type {classes.TableWidgetBase} */ controller.getWidget();
        const columnIndex = opt.data[0];
        if (tableWidget.isMultiColumnSortSupported()) {
          const multiSort = opt.data[1];
          this._multiColumnSort(controller, columnIndex, multiSort);
        } else {
          this._simpleSort(controller, columnIndex);
        }
      },

      /**
       * Simple sort
       * @param {classes.ControllerBase} controller
       * @param {number} columnIndex -1 means reset sort
       * @private
       */
      _simpleSort(controller, columnIndex) {
        const tableNode = controller.getAnchorNode();

        let sortType = "";
        if (columnIndex > -1) {
          if (columnIndex === tableNode.attribute("sortColumn")) {
            sortType = tableNode.attribute("sortType") === "asc" ? "desc" : "asc";
          } else {
            // if sorting a new column, always start with ascending
            sortType = "asc";
          }
        }

        const event = new cls.VMConfigureEvent(tableNode.getId(), {
          sortColumn: columnIndex,
          sortType: sortType
        });
        tableNode.getApplication().scheduler.eventVMCommand(event, tableNode);
      },

      /**
       * Apply a full set of sort rules, as built by the table configuration panel
       * @param {classes.ControllerBase} controller
       * @param {Object} opt - opt.data[0] is an ordered array of {colIndex, order, kind}
       * @private
       */
      _applySortRules(controller, opt) {
        const tableWidget = /** @type {classes.TableWidgetBase} */ controller.getWidget();
        const tableNode = /** @type {classes.TableNode} */ controller.getAnchorNode();
        const rules = opt.data[0] || [];

        let configureData = null;
        if (tableWidget.isMultiColumnSortSupported()) {
          const columnNodes = tableNode.getChildren("TableColumn");
          configureData = {
            sortSpec: rules
              .filter((rule) => columnNodes[rule.colIndex])
              .map((rule) => `${columnNodes[rule.colIndex].attribute("colName")}:${rule.order}/${rule.kind}`)
              .join(" ")
          };
        } else {
          // legacy simple sort: a single rule at most, through the sortColumn/sortType attributes
          const rule = rules[0];
          let sortType = "";
          if (rule) {
            sortType = rule.order === "+" ? "asc" : "desc";
          }
          configureData = {
            sortColumn: rule ? rule.colIndex : -1,
            sortType: sortType
          };
        }

        const event = new cls.VMConfigureEvent(tableNode.getId(), configureData);
        tableNode.getApplication().scheduler.eventVMCommand(event, tableNode);
      },

      /**
       * Multi-column sort
       * @param {classes.ControllerBase} controller
       * @param {number} columnIndex
       * @param {boolean} multiSort true if it is a multisort
       * @private
       */
      _multiColumnSort(controller, columnIndex, multiSort) {
        const tableNode = /** @type {classes.TableNode} */ controller.getAnchorNode();

        let newSortSpec = "";
        if (columnIndex === -1) {
          // columnIndex === -1 means reset multi-column sort
          newSortSpec = tableNode.getInitialSortSpecAttribute();
        } else {
          const columnNode = tableNode.getChildren("TableColumn")[columnIndex];
          const columnName = columnNode.attribute("colName");
          const sortSpecArray = tableNode.parseSortSpec();

          // Search if clicked column is already sorted
          let clickedColSort = sortSpecArray.find((sort) => sort.colName === columnName);
          const clickedOnUserColSort = Boolean(clickedColSort?.kind === "U");
          const clickedOnProgramColSort = Boolean(clickedColSort?.kind === "P");

          // Go through all sorted columns
          for (const sort of sortSpecArray) {
            if (clickedColSort === sort) {
              // col is the clicked column
              if (clickedOnProgramColSort) {
                // Change sort order
                clickedColSort.order = clickedColSort.order === "+" ? "-" : "+";
                // Add sort in colSpec
                newSortSpec += `${clickedColSort.colName}:${clickedColSort.order}/${clickedColSort.kind} `;
                // Invalid order to not send this col later
                clickedColSort.order = null;
              } else if (clickedOnUserColSort) {
                // Change sort order
                clickedColSort.order = sort.order === "+" ? "-" : null;
              }
            } else if (multiSort || clickedOnProgramColSort || sort.kind === "P") {
              // Add sortSpec for current column, if multiSort is activated
              // if click column is program sorted
              // if current column is program sorted
              newSortSpec += `${sort.colName}:${sort.order}/${sort.kind} `;
            }
          }

          // if clicked column is not sorted
          if (!clickedColSort) {
            // create an object to sort the clicked column
            clickedColSort = { colName: columnName, order: "+", kind: "U" };
          }
          if (clickedColSort.order) {
            // add the click column new sort at the end of sortSpec attribute
            newSortSpec += `${clickedColSort.colName}:${clickedColSort.order}/${clickedColSort.kind}`;
          }
        }

        // Send new sortSpec to VM
        const event = new cls.VMConfigureEvent(tableNode.getId(), {
          sortSpec: newSortSpec.trim()
        });
        tableNode.getApplication().scheduler.eventVMCommand(event, tableNode);
      }
    };
  });
});
